import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useEvent } from "../../contexts/EventContext";
import { useGroupNavigation } from "../../hooks/useGroupNavigation";

import { EventSelector } from "../events";
import { apiService } from "../../services/api";
import { Group, Person } from "../../types";
import "./navigation.css";

const Navigation: React.FC = () => {
  const { user, currentGroupSlug, availableGroups, isAuthenticated, logout } = useAuth();
  const { events, selectedEventId, isLoadingEvents, selectEvent } = useEvent();
  const { 
    getGroupPath 
  } = useGroupNavigation();
  const [group, setGroup] = useState<
    (Group & { persons: Person[]; isAdmin: boolean }) | null
  >(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && currentGroupSlug) {
      loadGroupData();
    } else if (isAuthenticated && !currentGroupSlug) {
      // Clear group data when no group is selected
      setGroup(null);
    }
  }, [isAuthenticated, currentGroupSlug]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isMobileMenuOpen) {
          closeMobileMenu();
        }
        if (isUserMenuOpen) {
          closeUserMenu();
        }
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      // Close user menu when clicking outside
      if (isUserMenuOpen && !(e.target as Element).closest(".nav-user")) {
        closeUserMenu();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen, isUserMenuOpen]);

  const loadGroupData = async () => {
    if (!currentGroupSlug) return;

    try {
      const groupData = await apiService.getGroupDetails(currentGroupSlug);
      setGroup(groupData);
    } catch (error) {
      console.error("Failed to load group data:", error);
    }
  };

  const handleLogout = async () => {
    try {
      closeUserMenu();
      closeMobileMenu();
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleGroupSwitch = () => {
    closeUserMenu();
    closeMobileMenu();
    navigate("/group-selection");
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const closeUserMenu = () => {
    setIsUserMenuOpen(false);
  };

  const handleEventSelect = (eventId: number) => {
    selectEvent(eventId);
    closeMobileMenu(); // Close mobile menu when event is selected
    navigate(getGroupPath('/dashboard')); // Navigate to dashboard when event changes
  };

  // Non mostrare la navigazione sulle pagine pubbliche
  const { isPublicRoute } = require('../../constants/routes');
  
  if (!isAuthenticated || isPublicRoute(location.pathname)) {
    return null;
  }

  return (
    <nav className="main-navigation">
      <div className="nav-container">
        {/* Header sempre visibile */}
        <div className="nav-header">
          <div className="nav-brand">
            <Link
              to={getGroupPath('/dashboard')}
              className="brand-link"
              onClick={closeMobileMenu}
            >
              <h1>CosaVorrei</h1>
              {group && <span className="group-name">{group.name}</span>}
            </Link>
          </div>

          {/* Event Selector - visible on both desktop and mobile */}
          <div className="nav-event-selector">
            {events.length > 0 && (
              <EventSelector
                events={events}
                selectedEventId={selectedEventId || undefined}
                onEventSelect={handleEventSelect}
                isLoading={isLoadingEvents}
              />
            )}
          </div>

          {/* Hamburger button */}
          <button
            className="hamburger-button"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span
              className={`hamburger-line ${isMobileMenuOpen ? "open" : ""}`}
            ></span>
            <span
              className={`hamburger-line ${isMobileMenuOpen ? "open" : ""}`}
            ></span>
            <span
              className={`hamburger-line ${isMobileMenuOpen ? "open" : ""}`}
            ></span>
          </button>
        </div>

        {/* Menu content - desktop sempre visibile, mobile collassabile */}
        <div className={`nav-content ${isMobileMenuOpen ? "mobile-open" : ""}`}>

          {/* Mostra nav-links se l'utente ha accesso al gruppo */}
          {user && group?.persons && (() => {
            // Calcola quante persone l'utente può gestire
            const userPersons = group.persons.filter(
              (p) =>
                p.userId === user.id || // Persone dell'utente stesso
                group.persons.some(
                  (parent) =>
                    parent.userId === user.id &&
                    p.parentPersonId === parent.id
                ) // Figli dell'utente
            );

            // Mostra sempre il menu admin se l'utente è admin
            // Mostra i menu delle liste solo se ci sono eventi e l'utente ha persone
            const showListMenus = events.length > 0 && userPersons.length > 0;
            const showAdminMenu = group?.isAdmin;
            const showEventMenu = events.length > 0;

            if (!showListMenus && !showAdminMenu && !showEventMenu) return null;

            return (
              <div className="nav-links">
                {/* Menu Evento - sempre visibile se ci sono eventi */}
                {events.length > 0 && (
                  <Link
                    to={getGroupPath('/dashboard')}
                    className={`nav-link ${
                      location.pathname.endsWith('/dashboard') ? "active" : ""
                    }`}
                    onClick={closeMobileMenu}
                  >
                    Home
                  </Link>
                )}
                
                {showListMenus && (
                  <>
                    <Link
                      to={getGroupPath('/my-lists')}
                      className={`nav-link ${
                        location.pathname.endsWith('/my-lists') ? "active" : ""
                      }`}
                      onClick={closeMobileMenu}
                    >
                      La mia lista
                    </Link>

                    <Link
                      to={getGroupPath('/my-reservations')}
                      className={`nav-link ${
                        location.pathname.endsWith('/my-reservations') ? "active" : ""
                      }`}
                      onClick={closeMobileMenu}
                    >
                      Le mie prenotazioni
                    </Link>
                  </>
                )}
                
                {/* Gestione gruppo su mobile - allo stesso livello degli altri menu */}
                {showAdminMenu && (
                  <Link
                    to={getGroupPath('/admin')}
                    className={`nav-link mobile-only ${
                      location.pathname.endsWith('/admin') ? "active" : ""
                    }`}
                    onClick={closeMobileMenu}
                  >
                    Gestione gruppo
                  </Link>
                )}
              </div>
            );
          })()}

          <div className="nav-user">
            <div className="user-menu-container">
              <button
                className="user-menu-trigger desktop-only"
                onClick={toggleUserMenu}
                aria-label="User menu"
              >
                <div className="user-avatar">
                  {user?.email?.charAt(0).toUpperCase()}
                </div>
                <svg
                  className={`user-menu-arrow ${isUserMenuOpen ? "open" : ""}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {/* Desktop: dropdown condizionale */}
              {isUserMenuOpen && (
                <div className="user-dropdown desktop-only">
                  <div className="user-dropdown-header">
                    <span className="user-dropdown-email">{user?.email}</span>
                  </div>
                  {/* Gestione gruppo solo su desktop nella dropdown */}
                  {group?.isAdmin && (
                    <button
                      onClick={() => {
                        closeUserMenu();
                        closeMobileMenu();
                        navigate(getGroupPath('/admin'));
                      }}
                      className="user-dropdown-item"
                    >
                      Gestione gruppo
                    </button>
                  )}
                  {availableGroups.length > 1 && (
                    <button
                      onClick={handleGroupSwitch}
                      className="user-dropdown-item group-switch-item"
                    >
                      Cambia gruppo
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="user-dropdown-item logout-item"
                  >
                    Esci
                  </button>
                </div>
              )}
              
              {/* Mobile: sempre visibile */}
              <div className="user-dropdown mobile-only mobile-always-open">
                <div className="user-dropdown-header mobile-only">
                  <span className="user-dropdown-email">{user?.email}</span>
                </div>
                {availableGroups.length > 1 && (
                  <button
                    onClick={handleGroupSwitch}
                    className="user-dropdown-item group-switch-item"
                  >
                    Cambia gruppo
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="user-dropdown-item logout-item"
                >
                  Esci
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Overlay per chiudere il menu su mobile */}
        {isMobileMenuOpen && (
          <button
            className="mobile-overlay"
            onClick={closeMobileMenu}
            onKeyDown={(e) => e.key === "Escape" && closeMobileMenu()}
            aria-label="Close menu"
          ></button>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
